/**
 * Base API Client
 * Core request method and error handling shared by all domain clients
 */
import { isPlatformHost } from '@foxet/shared';
import { useAuthStore } from '../store';

/**
 * On a tenant's CUSTOM domain the API is same-origin: the per-domain nginx
 * vhost proxies `/api/` to the API container (scripts/infra/foxet-provision-domain).
 * The prod bundle is built with an absolute `VITE_API_URL=https://api.foxet.ai/api`,
 * which on a custom domain would be a cross-origin call the API's static CORS
 * allowlist rejects — and it would also make the API see `Host: api.foxet.ai`
 * instead of the domain the visitor typed.
 *
 * So: platform hosts keep the build-time value, custom domains go relative.
 * Same bundle, no per-tenant build, no dynamic CORS allowlist.
 */
export const BASE_URL =
  typeof window !== 'undefined' && !isPlatformHost(window.location.host)
    ? '/api'
    : import.meta.env.VITE_API_URL || '/api';

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Get current auth headers (for SSE/WebSocket connections)
 */
export function getAuthHeaders(): Record<string, string> {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    return { 'Authorization': `Bearer ${accessToken}` };
  }
  return {};
}

/**
 * Get current access token (for SSE query params)
 */
export function getAccessToken(): string | null {
  const { accessToken } = useAuthStore.getState();
  return accessToken;
}

/**
 * Core API request method with auth handling and token refresh
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { accessToken, refresh, logout } = useAuthStore.getState();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only add Content-Type when there's a body
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 401) {
    const refreshed = await refresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${useAuthStore.getState().accessToken}`;
      const retry = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      if (retry.ok) return retry.json();
    }
    logout();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    // `Retry-After` is a HEADER, not a body field (chat/rate-limit.ts sets it
    // alongside the 429). Folding it into the body is the only way a caller
    // that sees just the ApiError can tell the user how long to wait.
    const retryAfter = response.headers.get('Retry-After');
    throw new ApiError(
      error.message || response.statusText,
      response.status,
      error.code,
      retryAfter ? { ...error, retryAfter } : error,
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export class ApiError extends Error {
  /**
   * `body` is the FULL parsed error payload, not just its message/code.
   *
   * Added for B11: the scheduler's 409 carries `nextSlots` — the times that
   * are actually free — and a UI that can only read `message` has to tell the
   * owner "that slot is gone" and then make them hunt for a replacement. Every
   * existing caller reads `message`/`status`/`code` and is unaffected; the
   * parameter is optional and additive.
   */
  constructor(message: string, public status: number, public code?: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}
